// Boss card prototype page (v0.8.9 prototype).
//
// Hidden route at /prototype/boss-cards. NOT linked from the
// public board or admin nav. Operator reviews the visual design
// here before we wire it into the live wall board.
//
// Two engines, same mock data:
//   ?engine=ascii (default) — monospace sprite, CSS keyframes
//   ?engine=3d             — procedural Three.js creature
//
// Layout:
//   - Header with notes on what to look for, plus engine toggle.
//   - Section A: all 6 sprites at their natural size, with
//     the boss name underneath, so you can see the variety.
//   - Section B: 4 mock boss cards in a row (active / vanquished /
//     survived / no-damage), full variant.
//   - Section C: same 4 mocks at compact size (no party list)
//     to compare density.
//   - Section D: a "wall board" mock — the full grid layout with
//     the new cards replacing the current boss row.

import { Link, useSearchParams } from 'react-router-dom';
import { BossCard } from '../boss/BossCard';
import type { BossCardMock, RenderEngine } from '../boss/BossCard';
import { SPRITES, spriteFor } from '../boss/sprites';

const MOCK_BOSSES: BossCardMock[] = [
  {
    id: 'b1',
    name: 'AMER Commercial v1.9, the Unspeakable',
    hp: 26,
    damage_dealt: 8,
    status: 'active',
    ends_at: '2026-10-15T00:00:00Z',
    users: [
      { user_id: 'u1', handle: 'rick', name: 'Rick Tonoli', damage: 5, color: '#c9a14a' },
      { user_id: 'u2', handle: 'yong', name: 'Yong Lee', damage: 2, color: '#3ecf8e' },
      { user_id: 'u3', handle: 'sam', name: 'Sam Park', damage: 1, color: '#7c5cff' },
    ],
  },
  {
    id: 'b2',
    name: 'APAC Commercial v15 - Input Mapping, the Horrid',
    hp: 29,
    damage_dealt: 18,
    status: 'active',
    ends_at: '2026-10-08T00:00:00Z',
    users: [
      { user_id: 'u4', handle: 'mei', name: 'Mei Chen', damage: 9, color: '#ef4444' },
      { user_id: 'u1', handle: 'rick', name: 'Rick Tonoli', damage: 6, color: '#c9a14a' },
      { user_id: 'u5', handle: 'dan', name: 'Dan Smith', damage: 3, color: '#f5a524' },
      { user_id: 'u6', handle: 'priya', name: 'Priya Iyer', damage: 1, color: '#7c5cff' },
    ],
  },
  {
    id: 'b3',
    name: 'EMEA Identity Federation, the Enduring',
    hp: 14,
    damage_dealt: 14,
    status: 'vanquished',
    ends_at: '2026-09-10T00:00:00Z',
    users: [
      { user_id: 'u2', handle: 'yong', name: 'Yong Lee', damage: 7, color: '#3ecf8e' },
      { user_id: 'u7', handle: 'arun', name: 'Arun Kumar', damage: 4, color: '#7c5cff' },
      { user_id: 'u1', handle: 'rick', name: 'Rick Tonoli', damage: 3, color: '#c9a14a' },
    ],
  },
  {
    id: 'b4',
    name: 'Convergence AIP, the Withering',
    hp: 16,
    damage_dealt: 4,
    status: 'survived',
    ends_at: '2026-08-24T00:00:00Z',
    users: [
      { user_id: 'u8', handle: 'lina', name: 'Lina Rossi', damage: 2, color: '#7c5cff' },
      { user_id: 'u9', handle: 'kenji', name: 'Kenji Tan', damage: 2, color: '#3ecf8e' },
    ],
  },
  {
    id: 'b5',
    name: 'Q4 Migration, the Looming',
    hp: 22,
    damage_dealt: 0,
    status: 'active',
    ends_at: '2026-11-01T00:00:00Z',
    users: [], // no damage yet — exercises the "awaiting challengers" placeholder
  },
];

export function BossCardPrototypePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const engine: RenderEngine = searchParams.get('engine') === '3d' ? '3d' : 'ascii';
  const setEngine = (e: RenderEngine) => {
    const next = new URLSearchParams(searchParams);
    if (e === 'ascii') next.delete('engine');
    else next.set('engine', e);
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-bg-base text-ink p-8">
      <div className="max-w-7xl mx-auto space-y-12">
        <div>
          <Link to="/public-board" className="text-xs text-ink-muted hover:text-ink">← back to public board</Link>
          <h1 className="text-3xl font-bold mt-2">Boss card prototype</h1>
          <p className="text-ink-muted mt-2 max-w-3xl">
            New visual treatment for boss fights. Each card has a 3-zone layout:
            creature on the left, name + HP + 3D bar on the right, and a
            party list below. Sprite assignment is stable per boss name
            via <code>stableHash()</code> — re-renders of the same boss
            always pick the same creature.
          </p>
          <div className="mt-4 flex items-center gap-2">
            <span className="text-xs text-ink-muted uppercase tracking-wide">Engine:</span>
            <button
              onClick={() => setEngine('ascii')}
              className={`px-3 py-1 text-xs rounded ${engine === 'ascii' ? 'bg-brand text-white' : 'bg-bg-elevated text-ink-muted hover:text-ink'}`}
            >
              ASCII
            </button>
            <button
              onClick={() => setEngine('3d')}
              className={`px-3 py-1 text-xs rounded ${engine === '3d' ? 'bg-brand text-white' : 'bg-bg-elevated text-ink-muted hover:text-ink'}`}
            >
              3D (Three.js)
            </button>
            <span className="text-xs text-ink-dim ml-2">
              {engine === '3d'
                ? 'Procedural low-poly creatures. Falls back to ASCII on WebGL failure.'
                : 'Pure CSS keyframes. 6 hand-authored silhouettes. Always works.'}
            </span>
          </div>
        </div>

        {/* Section A: sprite catalogue (only meaningful for ASCII) */}
        {engine === 'ascii' && (
          <section>
            <h2 className="text-lg font-semibold mb-3">A. Sprite catalogue ({SPRITES.length} silhouettes)</h2>
            <p className="text-sm text-ink-muted mb-4">
              Each sprite is 7 lines, ~10 chars wide. Designed to read at
              12px monospace on the wall board. Stable per boss via
              <code> stableHash(name) % 6</code>.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {SPRITES.map((s) => (
                <div key={s.id} className="bg-black/40 border border-white/10 rounded p-3 text-center">
                  <pre className={`m-0 ${s.accent} font-mono text-xs leading-[0.95] inline-block`}>{s.lines.join('\n')}</pre>
                  <div className="text-xs text-ink-muted mt-2">{s.label}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Section B: full cards in a row — the meat of the prototype */}
        <section>
          <h2 className="text-lg font-semibold mb-3">B. Full cards (5 mock bosses)</h2>
          <p className="text-sm text-ink-muted mb-4">
            Active, vanquished, survived, and the no-damage placeholder.
            Compare HP-bar colors, party-list density, and gold flash on the
            vanquished card. {engine === '3d' && (
              <span className="text-amber-300">
                Cards that fail to allocate a WebGL context (Chrome caps
                contexts per page; Pi 4 can hit the cap with this many
                cards) automatically drop down to the ASCII sprite.
              </span>
            )}
          </p>
          <div className="flex flex-wrap gap-4 items-start">
            {MOCK_BOSSES.map((b) => (
              <BossCard key={b.id} boss={b} sprite={spriteFor(b.name)} size="md" engine={engine} />
            ))}
          </div>
        </section>

        {/* Section C: compact variant. ASCII only on 3D page (Chrome
            WebGL context cap). */}
        {engine === 'ascii' && (
          <section>
            <h2 className="text-lg font-semibold mb-3">C. Compact (creature + name + HP, no party)</h2>
            <p className="text-sm text-ink-muted mb-4">
              For a denser row layout — fits more bosses on screen at the
              cost of the party list.
            </p>
            <div className="flex flex-wrap gap-3 items-start">
              {MOCK_BOSSES.map((b) => (
                <BossCard key={b.id} boss={b} sprite={spriteFor(b.name)} variant="compact" size="sm" engine={engine} />
              ))}
            </div>
          </section>
        )}

        {/* Section D: wall-board mock — in 3D mode, render only 3
            cards to stay under the WebGL context cap. */}
        <section>
          <h2 className="text-lg font-semibold mb-3">D. Wall-board mock (current chrome, new boss cards)</h2>
          <p className="text-sm text-ink-muted mb-4">
            Approximates the public-board layout: adventure card on the
            left, the new boss cards in a flex row above it. This is how
            it would look in production.
            {engine === '3d' && ' (Showing 3 cards in 3D mode to stay under Chrome\'s WebGL context cap.)'}
          </p>
          <div className="bg-bg-elevated border border-white/10 rounded-lg p-6">
            <h3 className="text-xl font-bold text-amber-300 mb-4">🏰 The Working Sprint of Fall Convergence</h3>
            <div className="mb-4 text-sm text-ink-muted">campaign progress · 47% · 8/17 challenges cleared</div>
            <div className="flex flex-wrap gap-3 items-start">
              {MOCK_BOSSES.slice(0, engine === '3d' ? 3 : 5).map((b) => (
                <BossCard key={b.id} boss={b} sprite={spriteFor(b.name)} size="sm" engine={engine} />
              ))}
            </div>
          </div>
        </section>

        {/* Section E: large for detail review. ASCII only on 3D page. */}
        {engine === 'ascii' && (
          <section>
            <h2 className="text-lg font-semibold mb-3">E. Large (read text + bar at 1×)</h2>
            <p className="text-sm text-ink-muted mb-4">
              Same cards at a larger size to read the type and check the
              bar/text contrast on a regular monitor.
            </p>
            <div className="flex flex-wrap gap-4 items-start">
              {MOCK_BOSSES.slice(0, 3).map((b) => (
                <BossCard key={b.id} boss={b} sprite={spriteFor(b.name)} size="lg" engine={engine} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
